import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Table, Button, Form, Row, Col, Card, Alert, Badge, Overlay, Popover, ListGroup, Spinner, Pagination } from 'react-bootstrap';
import api from '../../api/axios'; // Use centralized api instance
import AuthContext from '../../context/AuthContext';
import ServiceRecordDetailModal from '../../components/ServiceRecordDetailModal';
import { FaSearch } from 'react-icons/fa';
import { hasPermission } from '../../utils/permissions';
import { formatDateTime, formatShortDateTime, formatTime } from '../../utils/format';

const AllServiceRecords = () => {
    const { user } = useContext(AuthContext);
    const location = useLocation();
    
    const getFormattedDate = (date) => new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const [records, setRecords] = useState([]);
    const [filters, setFilters] = useState({
        startDate: location.state?.initialStartDate || getFormattedDate(startOfMonth),
        endDate: location.state?.initialEndDate || getFormattedDate(today),
        manufacturer: '',
        carModelId: '',
        courseId: '',
        optionId: '',
        status: 'completed,in_progress'
    });

    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1 });
    const [carModels, setCarModels] = useState([]);
    const [menus, setMenus] = useState([]);
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // API가 이미 displayOrder 순으로 정렬해서 반환 → 등장 순서 그대로 유지
    const manufacturers = React.useMemo(() => {
        const seen = new Set();
        const list = [];
        carModels.forEach(m => {
            if (m.manufacturer && !seen.has(m.manufacturer)) {
                seen.add(m.manufacturer);
                list.push(m.manufacturer);
            }
        });
        return list;
    }, [carModels]);

    const seriesModels = React.useMemo(() =>
        filters.manufacturer ? carModels.filter(m => m.manufacturer === filters.manufacturer) : [],
        [carModels, filters.manufacturer]
    );

    const [showRecordDetail, setShowRecordDetail] = useState(false);
    const [recordDetail, setRecordDetail] = useState(null);
    const [detailError, setDetailError] = useState('');

    const [showVehiclePopover, setShowVehiclePopover] = useState(false);
    const [popoverTarget, setPopoverTarget] = useState(null);
    const [popoverVehicle, setPopoverVehicle] = useState(null);
    const [isPopoverLoading, setIsPopoverLoading] = useState(false);

    const canEditRecords = hasPermission(user?.role, 'records:edit');
    const canDeleteRecords = hasPermission(user?.role, 'records:delete');
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

    const fetchDropdownData = useCallback(() => {
        api.get('/carmodels').then(res => setCarModels(res.data.data.filter(m => m.isActive))).catch(err => console.error("Error fetching car models:", err));
        api.get('/servicemenus').then(res => setMenus(res.data.data)).catch(err => console.error("Error fetching service menus:", err));
        api.get('/options').then(res => setOptions(res.data.data)).catch(err => console.error("Error fetching options:", err));
    }, []);

    const fetchRecords = useCallback(async (page = 1) => {
        if (!user) return;
        setIsLoading(true);
        try {
            const params = { ...filters, page, limit: 20, sort: 'desc' };
            if (!params.startDate || !params.endDate) {
                delete params.startDate;
                delete params.endDate;
            }
            const response = await api.get('/jobs', { params });
            const data = response.data.data;
            if (Array.isArray(data)) {
                setRecords(data);
                setPagination({ currentPage: 1, totalPages: 1 });
            } else {
                setRecords(data.records || []);
                setPagination({
                    currentPage: data.currentPage || 1,
                    totalPages: data.totalPages || 1,
                });
            }
        } catch (error) {
            console.error('AllServiceRecords - Error fetching records:', error);
        } finally {
            setIsLoading(false);
        }
    }, [user, filters]);

    useEffect(() => {
        if (user) {
            fetchDropdownData();
        }
    }, [user, fetchDropdownData]);
    
    useEffect(() => {
        if (user) {
            fetchRecords(1);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => {
            // 제조사 변경 시 시리즈(carModelId) 초기화
            if (name === 'manufacturer') return { ...prev, manufacturer: value, carModelId: '' };
            return { ...prev, [name]: value };
        });
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchRecords(1);
    };

    const handlePageChange = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= pagination.totalPages) {
            fetchRecords(pageNumber);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('정말로 이 기록을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
            try {
                await api.delete(`/jobs/${id}`);
                fetchRecords(pagination.currentPage);
            } catch (error) {
                console.error("Error deleting service record:", error);
                const msg = error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || '삭제에 실패했습니다.';
                alert(msg);
            }
        }
    };

    const handleShowRecordDetails = async (recordId) => {
        setDetailError('');
        try {
            const response = await api.get(`/jobs/${recordId}`);
            const job = response.data.data;
            if (!job) {
                setDetailError('기록을 찾을 수 없습니다.');
                return;
            }
            setRecordDetail(job);
            setShowRecordDetail(true);
        } catch (error) {
            console.error("Error fetching record details:", error);
            setDetailError('상세 내역을 불러오는 중 오류가 발생했습니다.');
        }
    };

    const handleVehiclePopover = async (event, plateNumber) => {
        if (popoverTarget === event.target) {
            setShowVehiclePopover(!showVehiclePopover);
            return;
        }
        setPopoverTarget(event.target);
        setIsPopoverLoading(true);
        setShowVehiclePopover(true);
        try {
            const response = await api.get(`/vehicles/${plateNumber}`);
            const vehicleData = response.data.data;
            if (vehicleData.vehicle_size_id && !vehicleData.sizeName) {
                 const sizeRes = await api.get('/vehiclesizes');
                 const size = sizeRes.data.data.sizes.find(s => s.id === vehicleData.vehicle_size_id);
                 vehicleData.sizeName = size ? size.sizeName : '미지정';
            }
            setPopoverVehicle(vehicleData);
        } catch (error) {
            console.error("Error fetching vehicle details:", error);
            setPopoverVehicle({ error: '차량 정보를 불러오는 데 실패했습니다.' });
        } finally {
            setIsPopoverLoading(false);
        }
    };

    const renderPagination = () => {
        const { currentPage, totalPages } = pagination;
        if (totalPages <= 1) return null;

        const pageBlockSize = 10;
        const currentBlock = Math.ceil(currentPage / pageBlockSize);
        const startPageInBlock = (currentBlock - 1) * pageBlockSize + 1;
        const endPageInBlock = Math.min(startPageInBlock + pageBlockSize - 1, totalPages);

        const items = [];

        // First Page
        items.push(
            <Pagination.First 
                key="first" 
                onClick={() => handlePageChange(1)} 
                disabled={currentPage === 1} 
            />
        );

        // Previous Block
        items.push(
            <Pagination.Prev 
                key="prev-block" 
                onClick={() => handlePageChange(startPageInBlock - 1)} 
                disabled={startPageInBlock === 1} 
            />
        );

        // Page numbers
        for (let number = startPageInBlock; number <= endPageInBlock; number++) {
            items.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>,
            );
        }

        // Next Block
        items.push(
            <Pagination.Next 
                key="next-block" 
                onClick={() => handlePageChange(endPageInBlock + 1)} 
                disabled={endPageInBlock === totalPages} 
            />
        );

        // Last Page
        items.push(
            <Pagination.Last 
                key="last" 
                onClick={() => handlePageChange(totalPages)} 
                disabled={currentPage === totalPages} 
            />
        );

        return <Pagination className="justify-content-center">{items}</Pagination>;
    };

    return (
        <>
            <div className="page-header">
                <div className="page-header-left">
                    <h3>서비스 이력</h3>
                    <p>전체 서비스 기록을 조회하고 관리합니다</p>
                </div>
            </div>
            {/* PC 필터 */}
            <Card className="mb-3 d-none d-md-block">
                <Card.Body>
                    <Form onSubmit={handleFilterSubmit}>
                        <Row className="align-items-end mb-2">
                            <Col md={3}><Form.Group><Form.Label>시작일</Form.Label><Form.Control type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} /></Form.Group></Col>
                            <Col md={3}><Form.Group><Form.Label>종료일</Form.Label><Form.Control type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} /></Form.Group></Col>
                            <Col md={3}><Form.Group><Form.Label>제조사</Form.Label><Form.Select name="manufacturer" value={filters.manufacturer} onChange={handleFilterChange}><option value="">모든 제조사</option>{manufacturers.map(m => <option key={m} value={m}>{m}</option>)}</Form.Select></Form.Group></Col>
                            <Col md={3}><Form.Group><Form.Label>시리즈</Form.Label><Form.Select name="carModelId" value={filters.carModelId} onChange={handleFilterChange} disabled={!filters.manufacturer}><option value="">{filters.manufacturer ? '모든 시리즈' : '제조사 먼저 선택'}</option>{seriesModels.map(m => <option key={m.id} value={m.id}>{m.modelName}</option>)}</Form.Select></Form.Group></Col>
                        </Row>
                        <Row className="align-items-end">
                            <Col md={3}><Form.Group><Form.Label>메뉴</Form.Label><Form.Select name="courseId" value={filters.courseId} onChange={handleFilterChange}><option value="">모든 메뉴</option>{menus.map(m => <option key={m.id} value={m.id}>{m.menuName}</option>)}</Form.Select></Form.Group></Col>
                            <Col md={3}><Form.Group><Form.Label>옵션</Form.Label><Form.Select name="optionId" value={filters.optionId} onChange={handleFilterChange}><option value="">모든 옵션</option>{options.map(o => <option key={o.id} value={o.id}>{o.optionName}</option>)}</Form.Select></Form.Group></Col>
                            <Col md={3}><Form.Group><Form.Label>상태</Form.Label><Form.Select name="status" value={filters.status} onChange={handleFilterChange}><option value="completed,in_progress">완료/진행</option><option value="completed">완료만</option><option value="noshow">노쇼</option><option value="canceled">취소</option><option value="draft,confirmed">예약</option><option value="">전체</option></Form.Select></Form.Group></Col>
                            <Col md={3} className="d-grid"><Button type="submit" disabled={isLoading}>{isLoading ? <Spinner size="sm" /> : '조회'}</Button></Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* 모바일 필터 */}
            <div className="d-md-none mb-3">
                <Form onSubmit={e => { handleFilterSubmit(e); setMobileFilterOpen(false); }}>
                    <div className="mob-filter-bar">
                        <div className="mob-filter-summary" onClick={() => setMobileFilterOpen(o => !o)}>
                            <span className="mob-filter-label">
                                {filters.startDate} ~ {filters.endDate}
                                {filters.manufacturer && ` · ${filters.manufacturer}`}
                                {filters.courseId && menus.find(m => m.id === parseInt(filters.courseId)) && ` · ${menus.find(m => m.id === parseInt(filters.courseId)).menuName}`}
                            </span>
                            <span className="mob-filter-toggle">{mobileFilterOpen ? '▲' : '▼'} 필터</span>
                        </div>
                        {mobileFilterOpen && (
                            <div className="mob-filter-body">
                                <div className="mob-filter-row">
                                    <label className="mob-filter-lbl">시작일</label>
                                    <Form.Control type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className="mob-filter-input" />
                                </div>
                                <div className="mob-filter-row">
                                    <label className="mob-filter-lbl">종료일</label>
                                    <Form.Control type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} className="mob-filter-input" />
                                </div>
                                <div className="mob-filter-row">
                                    <label className="mob-filter-lbl">제조사</label>
                                    <Form.Select name="manufacturer" value={filters.manufacturer} onChange={handleFilterChange} className="mob-filter-input">
                                        <option value="">모든 제조사</option>
                                        {manufacturers.map(m => <option key={m} value={m}>{m}</option>)}
                                    </Form.Select>
                                </div>
                                <div className="mob-filter-row">
                                    <label className="mob-filter-lbl">시리즈</label>
                                    <Form.Select name="carModelId" value={filters.carModelId} onChange={handleFilterChange} disabled={!filters.manufacturer} className="mob-filter-input">
                                        <option value="">{filters.manufacturer ? '모든 시리즈' : '제조사 먼저 선택'}</option>
                                        {seriesModels.map(m => <option key={m.id} value={m.id}>{m.modelName}</option>)}
                                    </Form.Select>
                                </div>
                                <div className="mob-filter-row">
                                    <label className="mob-filter-lbl">메뉴</label>
                                    <Form.Select name="courseId" value={filters.courseId} onChange={handleFilterChange} className="mob-filter-input">
                                        <option value="">모든 메뉴</option>
                                        {menus.map(m => <option key={m.id} value={m.id}>{m.menuName}</option>)}
                                    </Form.Select>
                                </div>
                                <div className="mob-filter-row">
                                    <label className="mob-filter-lbl">옵션</label>
                                    <Form.Select name="optionId" value={filters.optionId} onChange={handleFilterChange} className="mob-filter-input">
                                        <option value="">모든 옵션</option>
                                        {options.map(o => <option key={o.id} value={o.id}>{o.optionName}</option>)}
                                    </Form.Select>
                                </div>
                                <div className="mob-filter-row">
                                    <label className="mob-filter-lbl">상태</label>
                                    <Form.Select name="status" value={filters.status} onChange={handleFilterChange} className="mob-filter-input">
                                        <option value="completed,in_progress">완료/진행</option>
                                        <option value="completed">완료만</option>
                                        <option value="noshow">노쇼</option>
                                        <option value="canceled">취소</option>
                                        <option value="draft,confirmed">예약</option>
                                        <option value="">전체</option>
                                    </Form.Select>
                                </div>
                                <Button type="submit" className="w-100 mt-2" disabled={isLoading}>
                                    {isLoading ? <Spinner size="sm" /> : '조회'}
                                </Button>
                            </div>
                        )}
                    </div>
                </Form>
            </div>

            {detailError && <Alert variant="danger" dismissible onClose={() => setDetailError('')}>{detailError}</Alert>}

            <Card>
                <Card.Body className="p-0">
                    {/* PC 테이블 */}
                    <Table hover responsive className="mb-0 d-none d-md-table">
                        <thead>
                            <tr>
                                <th>날짜</th><th>차량번호</th><th>차종</th><th>메뉴</th><th>옵션</th><th>코멘트</th><th>결제수단</th><th>최종가격</th><th>관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr><td colSpan="9" className="text-center"><Spinner animation="border" /></td></tr>
                            ) : records.length > 0 ? (
                                records.map(record => (
                                    <tr key={record.id}>
                                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(record.startTime || record.created_at)}</td>
                                        <td>
                                            <Badge bg="primary" as={Link} to="/admin/vehicles" state={{ initialSearch: record.plateNumber }} className="text-decoration-none" style={{ fontVariantNumeric: 'tabular-nums' }}>{record.plateNumber}</Badge>
                                            <Button variant="link" size="sm" className="ms-1 p-0" title="차량 정보 미리보기" onClick={(e) => handleVehiclePopover(e, record.plateNumber)}><FaSearch /></Button>
                                        </td>
                                        <td>{record.manufacturer || '차종 미지정'} {record.modelName}</td>
                                        <td>{record.menuName}</td>
                                        <td className="text-muted">{record.selectedOptions || record.optionsText || '-'}</td>
                                        <td className="text-muted" style={{ maxWidth: 180, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }} title={record.comment || ''}>{record.comment || '-'}</td>
                                        <td><Badge bg="info">{record.paymentMethod}</Badge></td>
                                        <td className="fw-medium">{record.finalPrice != null ? parseInt(record.finalPrice).toLocaleString() + '원' : '-'}</td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                <Button variant="outline-secondary" size="sm" onClick={() => handleShowRecordDetails(record.id)}>상세</Button>
                                                {canEditRecords && <Button as={Link} to={`/admin/records/${record.id}/edit`} variant="outline-secondary" size="sm">수정</Button>}
                                                {canDeleteRecords && <Button variant="outline-secondary" size="sm" onClick={() => handleDelete(record.id)} className="text-danger">삭제</Button>}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="9">
                                    <div className="empty-state">
                                        <div className="empty-state-icon">📋</div>
                                        <div className="empty-state-title">조회된 기록이 없습니다</div>
                                        <div className="empty-state-desc">필터 조건을 변경해보세요</div>
                                    </div>
                                </td></tr>
                            )}
                        </tbody>
                    </Table>

                    {/* 모바일 카드 */}
                    <div className="d-md-none mob-list">
                        {isLoading ? (
                            <div className="text-center py-4"><Spinner size="sm" /> <span className="text-muted ms-2">불러오는 중...</span></div>
                        ) : records.length > 0 ? records.map(record => (
                            <div key={record.id} className="mob-card">
                                {/* 1행: 차량번호 + 금액 */}
                                <div className="mob-card-row1">
                                    <div className="mob-card-plate">
                                        <Badge bg="primary" as={Link} to="/admin/vehicles" state={{ initialSearch: record.plateNumber }} className="text-decoration-none mob-badge-plate">{record.plateNumber}</Badge>
                                    </div>
                                    <div className="mob-card-price">
                                        {record.finalPrice != null ? parseInt(record.finalPrice).toLocaleString() + '원' : '-'}
                                    </div>
                                </div>
                                {/* 2행: 날짜 + 차종 + 결제 */}
                                <div className="mob-card-row2">
                                    <span>{formatShortDateTime(record.startTime || record.created_at)}{record.endTime ? ` ~ ${formatTime(record.endTime)}` : ''}</span>
                                    <span className="mob-card-sep">·</span>
                                    <span>{(record.manufacturer || record.modelName) ? `${record.manufacturer || ''} ${record.modelName || ''}`.trim() : '차종 미지정'}</span>
                                    <span className="mob-card-sep">·</span>
                                    <span>{record.paymentMethod}</span>
                                </div>
                                {/* 3행: 메뉴 + 옵션 태그 */}
                                <div className="mob-card-tags">
                                    <span className="mob-tag mob-tag-menu">{record.menuName}</span>
                                    {(record.selectedOptions || record.optionsText || '').split(',').filter(Boolean).map((opt, i) => (
                                        <span key={i} className="mob-tag mob-tag-opt">{opt.trim()}</span>
                                    ))}
                                </div>
                                {/* 코멘트 */}
                                {record.comment && <div className="mob-card-comment">💬 {record.comment}</div>}
                                {/* 액션 */}
                                <div className="mob-card-actions">
                                    <button className="mob-action-btn" onClick={() => handleShowRecordDetails(record.id)}>상세</button>
                                    {canEditRecords && <Link to={`/admin/records/${record.id}/edit`} className="mob-action-btn">수정</Link>}
                                    {canDeleteRecords && <button className="mob-action-btn mob-action-danger" onClick={() => handleDelete(record.id)}>삭제</button>}
                                </div>
                            </div>
                        )) : (
                            <div className="empty-state">
                                <div className="empty-state-icon">📋</div>
                                <div className="empty-state-title">조회된 기록이 없습니다</div>
                                <div className="empty-state-desc">필터 조건을 변경해보세요</div>
                            </div>
                        )}
                    </div>

                    {!isLoading && records.length > 0 && (
                        <div className="px-3 pb-3">{renderPagination()}</div>
                    )}
                </Card.Body>
            </Card>

            <ServiceRecordDetailModal show={showRecordDetail} onHide={() => setShowRecordDetail(false)} record={recordDetail} />

            <Overlay show={showVehiclePopover} target={popoverTarget} placement="bottom" onHide={() => setShowVehiclePopover(false)} rootClose>
                <Popover id="vehicle-popover">
                    <Popover.Header as="h3">차량 상세 정보</Popover.Header>
                    <Popover.Body>
                        {isPopoverLoading ? <Spinner animation="border" size="sm" /> : (
                            popoverVehicle && (
                                popoverVehicle.error ? <Alert variant="danger">{popoverVehicle.error}</Alert> :
                                <ListGroup variant="flush">
                                    <ListGroup.Item><strong>차량번호:</strong> {popoverVehicle.plateNumber}</ListGroup.Item>
                                    <ListGroup.Item><strong>차종:</strong> {popoverVehicle.manufacturer || '차종 미지정'} {popoverVehicle.modelName}</ListGroup.Item>
                                    <ListGroup.Item><strong>차급:</strong> {popoverVehicle.vehicleClass || '-'}</ListGroup.Item>
                                    <ListGroup.Item><strong>사이즈:</strong> {popoverVehicle.sizeName || '미지정'}</ListGroup.Item>
                                    <ListGroup.Item><strong>코멘트:</strong> {popoverVehicle.comment || '-'}</ListGroup.Item>
                                </ListGroup>
                            )
                        )}
                    </Popover.Body>
                </Popover>
            </Overlay>
        </>
    );
};

export default AllServiceRecords;
